import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchMdIcon from '@untitled-ui/icons-react/build/esm/SearchMd';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import PlusIcon from '@untitled-ui/icons-react/build/esm/Plus';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch } from 'react-redux';
import {
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  OutlinedInput,
  Button,
  IconButton,
  Typography,
} from '@mui/material';
import { useMediaQuery } from 'react-responsive';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SearchIcon from '@mui/icons-material/Search';
import toast from 'react-hot-toast';
import CloseIcon from '@mui/icons-material/Close';

const RedesignUserListSearch = ({
  userListState,
  selectedUsers,
  onDeleteFromGroup,
  onAddToGroup,
  currentGroup,
  handlePageChange,
  currentUsersPage,
  usersCount,
  setCurrentUsersQuery,
  currentUsersQuery,
  currentUsersType,
  onTabChange,
}) => {
  const { t } = useTranslation();
  // const usersCount = useSelector((state) => state.User.usersCount);

  const tabs = [
    { label: t('views.redesignUserListSearch.tabs.all'), value: 'all' },
    { label: t('views.redesignUserListSearch.tabs.active'), value: 'isActive' },
    {
      label: t('views.redesignUserListSearch.tabs.blocked'),
      value: 'isBlocked',
    },
    { label: t('views.redesignUserListSearch.tabs.admin'), value: 'isAdmin' },
  ];

  const {
    state: listState,
    handleFiltersChange,
    handleSortChange,
  } = userListState;
  const { filters, sortBy, sortDir } = listState;
  // const dispatch = useDispatch();

  // const [currentUsersPage, setCurrentUsersPage] = useState(0)

  const sortByValue = useMemo(() => `${sortBy}_${sortDir}`, [sortDir, sortBy]);
  const currentTab = currentUsersType ?? 'all';

  const isMobile = useMediaQuery({ query: '(max-width: 786px)' });
  const isSearchContainer = useMediaQuery({ query: '(max-width: 1120px)' });

  const handleTabsChange = useCallback(
    (event, value) => {
      onTabChange?.(value);
    },
    [onTabChange],
  );

  // const handlePageChange = (type, value) => {
  //   if(typeof value !== 'undefined'){
  //     setCurrentUsersPage(value)
  //     dispatch(getAllUsers(value))
  //   }else{
  //     if(type == '+1'){
  //       if(currentUsersPage < Math.floor(usersCount/3)){
  //         setCurrentUsersPage(currentUsersPage+1)
  //         dispatch(getAllUsers(currentUsersPage+1))
  //       }
  //     }else{
  //       if(currentUsersPage > 0){
  //         setCurrentUsersPage(currentUsersPage-1)
  //         dispatch(getAllUsers(currentUsersPage-1))
  //       }
  //     }
  //   }
  // }

  const handleSearchQueryChange = useCallback(
    (event) => {
      handleFiltersChange({
        ...filters,
        query: event.target.value ?? undefined,
      });
    },
    [filters],
  );

  const handleSortSelect = useCallback((event) => {
    const value = event.target.value;
    const [sortBy, sortDir] = value.split('_');
    handleSortChange({ sortBy, sortDir });
  }, []);

  const handleAddUsersToGroup = useCallback(() => {
    onAddToGroup?.();
  }, [onAddToGroup]);

  const handleDeleteUsersFromGroup = useCallback(() => {
    onDeleteFromGroup?.();
  }, [onDeleteFromGroup]);

  const handleQuerySet = (e) => {
    e.preventDefault();
    if (e.target[0].value.length > 2) {
      setCurrentUsersQuery(e.target[0].value);
    } else {
      toast.error('To search, enter at least 3 characters');
    }
  };

  const handleQueryReset = () => {
    setCurrentUsersQuery('');
  };

  return (
    <>
      <Tabs
        indicatorColor='primary'
        onChange={handleTabsChange}
        scrollButtons='auto'
        sx={{ px: 3 }}
        textColor='primary'
        value={currentTab}
        variant='scrollable'
      >
        {tabs.map((tab) => (
          <Tab key={tab.value} label={tab.label} value={tab.value} />
        ))}
      </Tabs>
      <Divider />
      <Stack
        alignItems={isSearchContainer ? 'flex-start' : 'center'}
        direction={isSearchContainer ? 'column' : 'row'}
        flexWrap='wrap'
        justifyContent='space-between'
        spacing={3}
        sx={{ padding: 3 }}
      >
        <Box
          component='form'
          onSubmit={handleQuerySet}
          onReset={handleQueryReset}
          sx={{
            display: 'flex',
            gap: '10px',
          }}
        >
          <OutlinedInput
            defaultValue=''
            fullWidth
            sx={{ maxWidth: isMobile ? '100%' : '400px' }}
            // onChange={handleSearchQueryChange}
            name='itemName'
            placeholder={t('views.redesignUserListSearch.searchPlaceholder')}
          />
          <IconButton size='large' type='submit'>
            <SearchIcon color='primary' />
          </IconButton>
          {currentUsersQuery ? (
            <IconButton size='large' type='reset'>
              <CloseIcon color='error' />
            </IconButton>
          ) : null}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton onClick={() => handlePageChange('-1')} color='primary'>
            <ArrowBackIosIcon />
          </IconButton>
          <Typography>
            {currentUsersPage + 1}/{Math.floor(usersCount / 50) + 1}
          </Typography>
          <IconButton onClick={() => handlePageChange('+1')} color='primary'>
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: 'flex',
            minWidth: '420px',
            flexDirection: isSearchContainer ? 'column' : 'row',
          }}
        >
          {selectedUsers.length !== 0 && (
            <Button
              onClick={handleAddUsersToGroup}
              startIcon={
                <SvgIcon>
                  <PlusIcon />
                </SvgIcon>
              }
              variant='contained'
              sx={{
                width: '200px',
                height: '54px',
                marginRight: '20px',
                marginBottom: isSearchContainer
                  ? '24px !important'
                  : '0px !important',
              }}
            >
              {t('views.redesignUserListSearch.addToGroup')}
            </Button>
          )}
          {selectedUsers.length !== 0 && currentGroup && (
            <Button
              onClick={handleDeleteUsersFromGroup}
              startIcon={
                <SvgIcon>
                  <DeleteIcon />
                </SvgIcon>
              }
              variant='contained'
              sx={{
                width: '200px',
                height: '54px',
                marginRight: '20px',
                marginBottom: isSearchContainer
                  ? '24px !important'
                  : '0px !important',
              }}
            >
              {t('views.redesignUserListSearch.removeFromGroup')}
            </Button>
          )}
          <FormControl sx={{ width: '200px', marginLeft: 'auto' }} fullWidth>
            <InputLabel id='user-sort-by-label'>
              {t('views.redesignUserListSearch.sortBy')}
            </InputLabel>
            <Select
              labelId='user-sort-by-label'
              id='user-sort-by'
              value={sortByValue}
              label={t('views.redesignUserListSearch.sortBy')}
              onChange={handleSortSelect}
            >
              <MenuItem value={'email_desc'}>
                {t('views.redesignUserListSearch.sortOptions.emailDesc')}
              </MenuItem>
              <MenuItem value={'email_asc'}>
                {t('views.redesignUserListSearch.sortOptions.emailAsc')}
              </MenuItem>
              <MenuItem value={'name_desc'}>
                {t('views.redesignUserListSearch.sortOptions.nameDesc')}
              </MenuItem>
              <MenuItem value={'name_asc'}>
                {t('views.redesignUserListSearch.sortOptions.nameAsc')}
              </MenuItem>
              <MenuItem value={'createdAt_desc'}>
                {t('views.redesignUserListSearch.sortOptions.dateDesc')}
              </MenuItem>
              <MenuItem value={'createdAt_asc'}>
                {t('views.redesignUserListSearch.sortOptions.dateAsc')}
              </MenuItem>
              <MenuItem value={'status_desc'}>
                {t('views.redesignUserListSearch.sortOptions.statusDesc')}
              </MenuItem>
              <MenuItem value={'status_asc'}>
                {t('views.redesignUserListSearch.sortOptions.statusAsc')}
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Stack>
    </>
  );
};

export default RedesignUserListSearch;
